import { queryOptions } from '@tanstack/react-query';

import {
  apiGet,
  apiPatch,
  apiPost,
  fillPath,
  type ApiError,
  type BodyOf,
  type GetPath,
  type ResponseOf,
} from './http';
import { queryKeys } from './queryKeys';

/**
 * The person card and every write this surface makes about a person.
 *
 * All of it goes through `http.ts` over the one `apiClient`, and every request and response type is
 * derived from `schema.d.ts` — including the create body, which is `PersonIn` and therefore
 * **carries no tenant**: the backend resolves the tenant from the authenticated user
 * (`.claude/rules/tenancy.md`), and a hand-written body type is how a field like that gets added by
 * accident.
 *
 * Nothing here catches an `ApiError`. A 401 signs the app out (`queryClient.ts`), a 429 is read as a
 * throttle and a 404 on a record route is *"outside what you may see"* rather than *"no such
 * thing"* (backend TASK-029) — all three are decided once, in `http.ts` and `ApiErrorState`, and a
 * `try`/`catch` here would swallow exactly that.
 */

type PersonCardFromApi = ResponseOf<'/api/v1/people/{person_id}/', 'get'>;
type ConsiderationOnCardFromApi = PersonCardFromApi['considerations'][number];
type InterviewOnCardFromApi = ConsiderationOnCardFromApi['interviews'][number];

/**
 * The card as the screens may treat it — **the payload itself, unnarrowed** since backend TASK-104.
 *
 * ⚠️ **These were three hand-written types over generated ones**, and the reason was real: the document
 * typed both verdicts as `string | null` while `StatusDot` can only render four values, so this module
 * rebuilt the union with `Omit<…> & { … }` and mapped every row through a narrowing helper at the API
 * edge (`asRecommendation`, deleted with its last caller by BUG-038). That is the
 * shape `.claude/rules/frontend.md` forbids — *"types come from the backend's OpenAPI schema, never
 * hand-written"* — done because no client could fix it: the API had **three** typings of one value, and
 * `apps.people.schemas` had a stated reason for its `str` (it imported nothing from `apps.interviews`).
 *
 * The vocabulary moved to `apps.core.vocabularies`, every verdict field is the enum, and a draft's
 * empty string is `null` in every payload — so the generated types now say what the screens need and
 * these three aliases are the document's own shapes. Deleting them rather than adjusting them is the
 * point: a workaround kept after its reason is gone is the next reader's puzzle.
 */
export type InterviewOnCard = InterviewOnCardFromApi;

/**
 * ⚠️ **A union since Sprint 24, and the screens hold it as one type** (`ConsiderationOnCardRead`).
 * One run, two shapes, decided by the caller's own reach — never by a flag and never by a status
 * code. Narrow it with `importedCommentsAreOpen` below and with nothing else.
 */
export type ConsiderationOnCard = ConsiderationOnCardFromApi;

export type PersonCard = PersonCardFromApi;

/**
 * **The shape a reader ADR 0006's gate has opened receives** — `ConsiderationWithImportedCommentsOut`.
 *
 * It is `ConsiderationOnCardOut` plus one key, `imported_comments`, and that key is the whole of the
 * gate on this surface (ADR 0020 decision 4). An imported comment is not feedback anybody in Panel
 * wrote, and ADR 0006's wording does not cover it — the decision was taken on its **reasoning**: the
 * argument is about the reader, and a hiring manager's *"strong hire, move fast"* anchors a first-time
 * interviewer exactly as a colleague's verdict would.
 */
type ConsiderationWithImportedCommentsFromApi = Extract<
  ConsiderationOnCardFromApi,
  { imported_comments: unknown }
>;

/**
 * **The blinded shape** — `ConsiderationOnCardOut`, which carries **no key at all** for imported
 * comments rather than an empty list.
 *
 * ⚠️ **That is the contract and not an implementation detail.** An empty array would make *"this
 * candidate has no imported comments"* and *"you may not read them yet"* one payload, which no client
 * could tell apart; the server refuses the row into this shape instead
 * (`ConsiderationWithImportedCommentsOut._refuse_a_row_whose_gate_is_closed`). `imported_attachments`
 * is on **both** shapes deliberately: the gate hides assessments, not facts about the candidate, and a
 * CV is the document an interviewer reads to prepare.
 */
export type ConsiderationWithoutImportedComments = Exclude<
  ConsiderationOnCardFromApi,
  { imported_comments: unknown }
>;

/**
 * `ImportedCommentOut` — an id and the comment's text, and nothing about who wrote it or when.
 *
 * ⚠️ **The text is Panel's plain-text reading of what ADP held, not the bytes ADP sent** — this line
 * said *"the text ADP held"* until 2026-11-03, and that wording is **retracted** (BUG-120). ADP's
 * `textValue` is rich text; `apps/adp/rich_text.py` converts it at import, which is why the card
 * renders real line breaks and never markup. The comment's **identity** is unchanged, being a digest
 * of exactly what ADP sent.
 */
export type ImportedComment = ConsiderationWithImportedCommentsFromApi['imported_comments'][number];

/**
 * `ImportedAttachmentOut` — an id and the **filename**, which is the candidate's own and is personal
 * data. There is **no URL and no bytes on this payload**, and `anonymize_person` deletes the document
 * and blanks the name.
 *
 * ⚠️ **The clause that stood here until 2026-10-28 — *"nothing in the product serves these files"* —
 * is retracted (backend TASK-177).** `importedAttachmentUrl`, five lines down, is the route that
 * serves them, behind this very card's reach rule. What survives is the half the sentence rested on:
 * the **storage root** is mounted by nothing, so those bytes have exactly one address and the
 * application itself guards it. A URL in this payload would be a second place deciding who reads a CV.
 */
export type ImportedAttachment = ConsiderationOnCardFromApi['imported_attachments'][number];

/**
 * ⚠️ **The one address these bytes have** — `GET /api/v1/people/{person_id}/attachments/{id}/`
 * (backend TASK-177, ADR 0020 decision 3).
 *
 * **A URL rather than a request, and that is the shape the thing needs.** A download is a navigation
 * the browser performs with the session cookie, not a body this client parses: `apiGet` would hand a
 * screen a PDF as a string. So this builds the address and `<a href>` does the rest — same origin,
 * same session, and `.claude/rules/frontend.md`'s rule is kept where it bites, because the **path**
 * still comes from the generated document (`GetPath`) and a route renamed on the backend is a `tsc`
 * error here rather than a 404 in a browser.
 *
 * ⚠️ **The bytes are reachable through this and through nothing else.** They are stored under
 * `settings.IMPORTED_ATTACHMENTS_ROOT`, a volume `nginx` deliberately does not mount — `location
 * /media/` serves its whole root to the open internet, which is right for a logo rendered on the
 * sign-in page and wrong for a CV. So the document passes a session, a group and a reach rule, or it
 * reaches nobody.
 *
 * **A 404 covers four cases byte for byte** — another tenant's attachment, an id that never existed,
 * one on a run this `Interviewer` was not assigned to, and a document ADR 0009 has erased, whose row
 * survives with nothing to serve. A 403 would confirm that a candidate exists.
 */
const IMPORTED_ATTACHMENT_PATH =
  '/api/v1/people/{person_id}/attachments/{attachment_id}/' satisfies GetPath;

export const importedAttachmentUrl = (personId: number, attachmentId: number) =>
  fillPath(IMPORTED_ATTACHMENT_PATH, { person_id: personId, attachment_id: attachmentId });

/**
 * ⚠️ **The second discrimination in the client, and it is the first one's shape rather than a new
 * idea** (ADR 0020 decision 4; `isReadable` in `api/interviews.ts` is the first).
 *
 * It decides on **`imported_comments`** — a key the blinded shape cannot carry, because
 * `ConsiderationOnCardOut` is a separate class rather than the readable one with a key nulled. The
 * negative branch is the load-bearing half: it narrows to `ConsiderationWithoutImportedComments`, so
 * reading `imported_comments` off it is a **`tsc` error** rather than an empty render.
 *
 * ⚠️ **There is no second discriminator anywhere on this surface, and there must not be.** §6.5 reads
 * this one and renders nothing — no heading, no count, no empty sentence — when it answers `false`: a
 * blinded reader must see no sign that an imported comment exists, which a *"nothing here yet"* line
 * would give away as surely as the text would.
 */
export function importedCommentsAreOpen<T extends ConsiderationWithoutImportedComments>(
  consideration: T,
): consideration is Extract<T, { imported_comments: unknown }> {
  return 'imported_comments' in consideration;
}

/**
 * `PossibleDuplicateWarningOut` — **a stored warning an import raised** (ADR 0021), and the
 * compensating surface that makes *"an automated import never merges"* an honest decision rather than
 * a silent loss.
 *
 * ⚠️ **It is not `PossibleDuplicate` and must never become it.** That one is the body of a 409 — a
 * bounded disclosure inside one request — and it carries the other person's name, contacts and runs.
 * This row **outlives** the request and is read by whoever opens the card months later, so it carries
 * the other card's **id** and the **names** of the keys that matched, and nothing else. Holding no
 * personal data is why ADR 0009 has nothing to clear on it, and it is why §6.5 renders a link rather
 * than a name: the reader fetches that card through the ordinary tenant-scoped person route, which was
 * already theirs to read.
 *
 * Only **open** warnings are ever in this list; an acknowledged one leaves the card.
 */
export type PossibleDuplicateWarning = PersonCardFromApi['possible_duplicates'][number];

/**
 * Close a warning an import raised: *"I looked at both cards, they are two people."*
 *
 * **Admin or Recruiter** — `Route("post", f"{POSSIBLE_DUPLICATE}acknowledge/", None, (401, 403, 200,
 * 200, 403, 403))` in `backend/tests/test_permission_matrix.py`. An `Interviewer` is 403 even for a
 * candidate they are assigned to: reach is not permission, and this is a judgement about the tenant's
 * candidate records.
 *
 * ⚠️ **It merges nothing, and it is not ADR 0014's acknowledgement flag.** That flag is a field of a
 * request body meaning *"create the second card anyway"*, answered before anything exists; both cards
 * already exist here and what is recorded is that somebody read the warning. Nothing in this product
 * merges two people.
 *
 * ⚠️ **It is a route rather than a client-side dismissal, and that is the point of it** (ADR 0021): a
 * warning that un-warns itself on a reload is not a warning. The caller invalidates the card and reads
 * the answer back off the refetch — the server stops sending an acknowledged warning at all.
 *
 * **Idempotent**: a second call answers 200 and keeps the first acknowledgement's actor and timestamp.
 */
export const acknowledgePossibleDuplicate = (possibleDuplicateId: number) =>
  apiPost('/api/v1/people/possible-duplicates/{possible_duplicate_id}/acknowledge/', undefined, {
    params: { possible_duplicate_id: possibleDuplicateId },
  });

/**
 * One request, and the whole card comes out of it. The card must never ask `/api/v1/interviews/` for
 * anything: ADR 0006 keeps another person's unsubmitted feedback out of the **response**, and
 * fetching the interview list to filter it in the component is the edit §6.5 predicts by name.
 */
export async function fetchPersonCard(personId: number): Promise<PersonCard> {
  return apiGet('/api/v1/people/{person_id}/', { params: { person_id: personId } });
}

/**
 * ⚠️ **Keyed on the viewer** (`queryKeys.person`), because the summary row and every interview row
 * are computed over the caller's own blind-filtered set (ADR 0006): two viewers legitimately receive
 * different bodies from `/api/v1/people/{id}/`, and a cache keyed on the URL alone would serve one
 * of them the other's figures with every backend test still green.
 *
 * No `placeholderData` here either — see `considerationsQuery` for what that resolves from.
 */
export function personQuery(viewerId: number, personId: number) {
  /*
   * The error type is named for the same reason `considerationsQuery` names it: every failure of this
   * route *is* an `ApiError` (`apiGet` converts anything else before it leaves `http.ts`), and
   * `ApiErrorState` is what decides which §8 state a failure is. Without it §6.5 would hold a bare
   * `Error` and have to cast at the exact point the offline / forbidden / throttle distinction is
   * made — and on this route that distinction is the 404-not-403 rule (backend TASK-029).
   */
  return queryOptions<PersonCard, ApiError>({
    queryKey: queryKeys.person(viewerId, personId),
    queryFn: () => fetchPersonCard(personId),
  });
}

/** `PersonIn` — `full_name`, `email`, `phone`, `source`, `acknowledge_possible_duplicates`. */
export type NewPerson = BodyOf<'/api/v1/people/', 'post'>;

/** `PersonOut` — what a create answers: the person, with no derived value and no interview. */
export type Person = ResponseOf<'/api/v1/people/', 'post'>;

/**
 * `PersonIn` **without the override** — the body of a first attempt, and the reason this type exists.
 *
 * The document gives `acknowledge_possible_duplicates` a default of `false`, so the endpoint accepts
 * a body that omits it; the generated type marks every field required, which is stricter than the
 * route. Omitting it is not tidiness (ADR 0014): a first attempt that already carries the override —
 * even as `false` — is one character away from carrying it as `true`, and then the detection is
 * decorative. So the flag exists in exactly one place, `createPersonDespiteDuplicates`.
 */
export type NewPersonAttempt = Omit<NewPerson, 'acknowledge_possible_duplicates'>;

/**
 * The enforcement point of *one person, one card* (feedback protection #7, ADR 0002 + ADR 0014).
 *
 * This body carries **no** `acknowledge_possible_duplicates` at all, so a match answers **409** with
 * the possible duplicates and writes nothing. The 409 is not an error state to route around — it is
 * the interaction §6.4 specifies, and `AddCandidateDialog` renders it as one.
 */
export const createPerson = (person: NewPersonAttempt) =>
  // The one assertion here, and its reason is `NewPersonAttempt`'s docblock: the generated type
  // requires a field the endpoint defaults. Sending it would defeat the point of not sending it.
  apiPost('/api/v1/people/', person as NewPerson);

/**
 * §6.4's **secondary** action — *"create a separate person"* — and the only place the override is
 * spelled at all (ADR 0014: *"The acknowledgement must be explicit; omitting it warns again."*).
 *
 * Two people genuinely can share a key — a family email address, a father and son with the same
 * name, an agency's phone number — so this path exists by design. It is a second, deliberate call
 * made after the warning has been read, never the first one.
 */
export const createPersonDespiteDuplicates = (person: NewPersonAttempt) =>
  apiPost('/api/v1/people/', { ...person, acknowledge_possible_duplicates: true });

/** `DuplicateCheckIn` — the three match keys, and a blank one matches nothing (ADR 0014). */
export type DuplicateCheck = BodyOf<'/api/v1/people/check-duplicates/', 'post'>;

/**
 * One entry of `possible_duplicates`: the person, **which keys matched** (`matched_on`) and their
 * considerations — so §6.4's sentence can say *why* it matched and not only *that* it did.
 *
 * The same entry type comes back from the pre-check and from the 409 of `POST /people/`, which is
 * what lets one component render both paths (`DuplicateWarning`).
 */
export type PossibleDuplicate = ResponseOf<
  '/api/v1/people/check-duplicates/',
  'post'
>['possible_duplicates'][number];

/**
 * A **POST with a body**, and that is the point: a name, an email or a phone number in a query
 * string outlives the request in every access log on the path (`.claude/rules/security.md`). The one
 * place this surface accepts personal data in a URL is `?search=`, which the backend chose
 * deliberately so a list stays linkable.
 */
export const checkDuplicates = (keys: DuplicateCheck) =>
  apiPost('/api/v1/people/check-duplicates/', keys);

/** `PersonUpdate` — every field optional, and an explicit `null` is answered 422 by the API. */
type PersonEditBody = BodyOf<'/api/v1/people/{person_id}/', 'patch'>;

/**
 * The fields §6.5's *Edit* may change — **without the override**, for the reason
 * `NewPersonAttempt` records one screen over: an edit that already carries
 * `acknowledge_possible_duplicates` (even as `false`) is one character away from carrying it as `true`,
 * and then the duplicate check is decorative. The flag exists in exactly one place, below.
 */
export type PersonEdit = Omit<PersonEditBody, 'acknowledge_possible_duplicates'>;

/**
 * Edit a person. ⚠️ **This can answer 409 with `possible_duplicates` since backend TASK-109** — the
 * same refusal the create has, because an *edit* could otherwise reach the duplicate a *create*
 * refuses (ADR 0002, feedback protection #7).
 *
 * This body carries **no** `acknowledge_possible_duplicates` at all, exactly as `createPerson` does, so
 * a match answers 409 and writes nothing. §6.5's dialog renders that refusal as the interaction ADR
 * 0014 specifies and offers *save anyway* through the helper below — never through this one.
 */
export const updatePerson = (personId: number, changes: PersonEdit) =>
  apiPatch('/api/v1/people/{person_id}/', changes as PersonEditBody, {
    params: { person_id: personId },
  });

/**
 * §6.5's **secondary** action — *"save the changes anyway"* — and the only place the edit's override is
 * spelled (ADR 0014: *"The acknowledgement must be explicit; omitting it warns again."*).
 *
 * ⚠️ **It sends the same changed fields a second time**, with the flag on top: the first `PATCH` wrote
 * nothing, so this is not a partial re-application of an edit that half-landed. The reasons two cards
 * may legitimately share a key are the create's — a family email address, a father and son with the
 * same name, an agency's phone number — and BUG-029 adds the one only an *edit* has: an acknowledged
 * pair goes on being edited on the key it already shares, because the server subtracts the pre-edit
 * match set (`duplicates_of_edit`) rather than refusing the pair for ever.
 */
export const updatePersonDespiteDuplicates = (personId: number, changes: PersonEdit) =>
  apiPatch(
    '/api/v1/people/{person_id}/',
    { ...changes, acknowledge_possible_duplicates: true },
    { params: { person_id: personId } },
  );

/** `ConsiderationIn` — the vacancy, and nothing else: `status` is not writable (ADR 0010). */
export type NewConsideration = BodyOf<'/api/v1/people/{person_id}/considerations/', 'post'>;

/** `ConsiderationRowOut` — the run that was opened, in §6.3's row shape. */
export type ConsiderationOpened = ResponseOf<'/api/v1/people/{person_id}/considerations/', 'post'>;

/**
 * A second run at another vacancy on the **existing** card — §6.4's primary answer to a duplicate,
 * and the reason ADR 0002 splits `Person` from `Consideration` at all.
 */
export const openConsideration = (personId: number, consideration: NewConsideration) =>
  apiPost('/api/v1/people/{person_id}/considerations/', consideration, {
    params: { person_id: personId },
  });

/**
 * The product's only erasure path (ADR 0009, feedback protection #6), **Admin only** on the server
 * (`(401, 403, 403, 200, 403, 403)`) — a Recruiter who may create and edit the very person is
 * refused here.
 *
 * It answers `PersonOut`, which is **not** the card: it carries no derived values and no interviews.
 * A caller invalidates and refetches the card instead of writing this answer into its cache.
 */
export const anonymizePerson = (personId: number) =>
  apiPost('/api/v1/people/{person_id}/anonymize/', undefined, {
    params: { person_id: personId },
  });
